import { ref } from 'vue';

import type { Ref } from 'vue';

export interface RetrierParams<T = any> {
  /** Функция для выполнения с повторными попытками */
  func: () => Promise<T>;
  /** Таймаут в миллисекундах */
  timeout?: number;
  /** Максимальное количество попыток */
  maxAttempts?: number;
  /** Задержка между попытками в мс */
  delay?: number;
  /** Чтобы проверять успешность (успешный успех) результата */
  isSuccess?: (result: T) => boolean;
  /** Колбек при успешном выполнении */
  onSuccess?: (result: T) => void;
  /** Колбек при ошибке/таймауте */
  onError?: (error: Error | string) => void;
  /** Колбек на каждую попытку */
  onAttempt?: (attemptNumber: number, result?: T) => void;
}

export interface RetrierResult<T = any> {
  /** Результат выполнения функции (если успешно) */
  result?: T;
  /** Произошла ли ошибка */
  isError: boolean;
  /** Сообщение об ошибке */
  errorMessage?: string;
  /** Было ли превышено время ожидания */
  isTimeout: boolean;
  /** Было ли отменено пользователем */
  isCancelled: boolean;
  /** Количество выполненных попыток */
  attemptCount: number;
}

export interface RetrierState {
  /** Состояние загрузки */
  isLoading: Ref<boolean>;
  /** Текущее количество попыток */
  attemptCount: Ref<number>;
  /** Максимальное количество попыток */
  maxAttempts: Ref<number>;
  /** Функция для отмены выполнения */
  cancel: () => void;
}

const FAILURE_MESSAGES = {
  timeout: 'Время ожидания истекло',
  cancelled: 'Операция отменена',
  attemptsExceeded: 'Превышено максимальное количество попыток',
} as const;

interface RetrierFailureOptions {
  attemptCount: number;
  errorMessage: string;
  isTimeout?: boolean;
  isCancelled?: boolean;
}

const failureResult = ({
  attemptCount,
  errorMessage,
  isTimeout = false,
  isCancelled = false,
}: RetrierFailureOptions): RetrierResult<never> => ({
  isError: true,
  isTimeout,
  isCancelled,
  attemptCount,
  errorMessage,
});

const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

interface NormalizedRetrierParams<T> {
  func: () => Promise<T>;
  timeout: number;
  maxAttempts: number;
  delay: number;
  isSuccess: (result: T) => boolean;
  onSuccess?: (result: T) => void;
  onError?: (error: Error | string) => void;
  onAttempt?: (attemptNumber: number, result?: T) => void;
}

const normalizeParams = <T>(params: RetrierParams<T>): NormalizedRetrierParams<T> => {
  const {
    func,
    timeout = 60000,
    maxAttempts = 4,
    delay = 2000,
    isSuccess = () => true,
    onSuccess,
    onError,
    onAttempt,
  } = params;

  return {
    func,
    timeout,
    maxAttempts,
    delay,
    isSuccess,
    onSuccess,
    onError,
    onAttempt,
  };
};

export function createRetrier () {
  const isLoading = ref(false);
  const attemptCount = ref(0);
  const maxAttempts = ref(0);
  const isCancelled = ref(false);

  const cancel = () => {
    isCancelled.value = true;
    isLoading.value = false;
  };

  const hasAttemptsLeft = (limit: number): boolean => attemptCount.value < limit;

  const finishWithFailure = (failure: RetrierFailureOptions): RetrierResult<never> => {
    isLoading.value = false;
    return failureResult(failure);
  };

  const buildTimeoutResult = <T>(options: NormalizedRetrierParams<T>): RetrierResult<T> => {
    options.onError?.(FAILURE_MESSAGES.timeout);
    return finishWithFailure({
      attemptCount: attemptCount.value,
      errorMessage: FAILURE_MESSAGES.timeout,
      isTimeout: true,
    });
  };

  const runAttempt = async <T>(options: NormalizedRetrierParams<T>): Promise<RetrierResult<T> | undefined> => {
    attemptCount.value++;
    const result = await options.func();
    options.onAttempt?.(attemptCount.value, result);

    if (!options.isSuccess(result)) {
      return undefined;
    }

    isLoading.value = false;
    options.onSuccess?.(result);
    return {
      result,
      isError: false,
      isTimeout: false,
      isCancelled: false,
      attemptCount: attemptCount.value,
    };
  };

  const handleAttemptError = <T>(
    options: NormalizedRetrierParams<T>,
    error: unknown,
  ): RetrierResult<never> | undefined => {
    options.onAttempt?.(attemptCount.value);

    console.error(`Ошибка на попытке ${ attemptCount.value }:`, error);

    if (hasAttemptsLeft(options.maxAttempts)) {
      return undefined;
    }

    const normalizedError = error instanceof Error ? error : new Error(String(error));
    options.onError?.(normalizedError);
    return finishWithFailure({
      attemptCount: attemptCount.value,
      errorMessage: normalizedError.message,
    });
  };

  const performIteration = async <T>(
    options: NormalizedRetrierParams<T>,
    startTime: number,
  ): Promise<RetrierResult<T> | undefined> => {
    if (Date.now() - startTime > options.timeout) {
      return buildTimeoutResult(options);
    }

    try {
      const success = await runAttempt(options);

      if (success) {
        return success;
      }

      if (hasAttemptsLeft(options.maxAttempts) && !isCancelled.value) {
        await sleep(options.delay);
      }
    } catch (error) {
      const failure = handleAttemptError(options, error);

      if (failure) {
        return failure;
      }

      if (!isCancelled.value) {
        await sleep(options.delay);
      }
    }

    return undefined;
  };

  const buildLoopExitResult = <T>(options: NormalizedRetrierParams<T>): RetrierResult<T> => {
    if (isCancelled.value) {
      return failureResult({
        attemptCount: attemptCount.value,
        errorMessage: FAILURE_MESSAGES.cancelled,
        isCancelled: true,
      });
    }

    options.onError?.(FAILURE_MESSAGES.attemptsExceeded);
    return failureResult({
      attemptCount: attemptCount.value,
      errorMessage: FAILURE_MESSAGES.attemptsExceeded,
    });
  };

  const execute = async <T = any>(params: RetrierParams<T>): Promise<RetrierResult<T>> => {
    const options = normalizeParams(params);

    isCancelled.value = false;
    isLoading.value = true;
    attemptCount.value = 0;
    maxAttempts.value = options.maxAttempts;

    const startTime = Date.now();

    while (!isCancelled.value && hasAttemptsLeft(options.maxAttempts)) {
      const outcome = await performIteration(options, startTime);

      if (outcome) {
        return outcome;
      }
    }

    isLoading.value = false;

    return buildLoopExitResult(options);
  };

  const state: RetrierState = {
    isLoading,
    attemptCount,
    maxAttempts,
    cancel,
  };

  return {
    execute,
    state,
  };
}

export const retrier = async <T = any>(params: RetrierParams<T>): Promise<RetrierResult<T>> => {
  const { execute } = createRetrier();
  return execute(params);
};
